<template lang="pug">
#home-for-waiters-service.mb-0
  //- waiters information
  .row.facility-base-info
    .home-box.col-sm-6.col-md-4.col-xl-2
      HomeCountBox(title="待機者数" :count="waiterCount" :isBusy="isBusyWaiterCount" unit="人")
    .home-box.col-sm-6.col-md-4.col-xl-2
      HomeCountBox(title="募集ページ閲覧（先月）" :count="accessCountOfLastMonth" :isBusy="isBusyAccessCountOfLastMonth" unit="人")
    .home-box.col-sm-6.col-md-4.col-xl-2
      HomeCountBox(title="募集ページ閲覧（今月）" :count="accessCountOfThisMonth" :isBusy="isBusyAccessCountOfThisMonth" unit="人")
    .home-box.col-sm-6.col-md-4.col-xl-2
      HomeCountBox(title="入所申込み（先月）" :count="applicationsCountOfLastMonth" :isBusy="isBusyApplicationsCountOfLastMonth" unit="人")
    .home-box.col-sm-6.col-md-4.col-xl-2
      HomeCountBox(title="入所申込み（今月）" :count="applicationsCountOfThisMonth" :isBusy="isBusyApplicationsCountOfThisMonth" unit="人")
    .home-box.col-sm-6.col-md-4.col-xl-2
      HomeCountBox(title="入所申込み（昨日）" :count="applicationsCountOfYesterday" :isBusy="isBusyApplicationsCountOfYesterday" unit="人")

  //- graph
  .row.graphs.d-flex.flex-row.flex-wrap
    .carelevels-and-roomtypes.col-md-12.col-lg-5
      .d-flex.flex-column
        //- care level graph
        .carelevels.box.box-margin
          .box-header.title 待機者：要介護度
          .box-body
            DoughnutChart(:chart-data="careLevelsGraph" :height="careLevelsGraphHeight" labelSuffix="人")
            Loading(ref="careLevelsGraphLoading")

        //- room type graph
        .roomtypes.box.box-margin
          .box-header.title 待機者：申込み部屋種類
          .box-body
            BarChart(:chart-data="roomTypesGraph" :height="roomTypesGraphHeight")
            Loading(ref="roomTypesGraphLoading")
    
    //- waiters graph
    .waiters-graph.col-md-12.col-lg-7
      .box.box-margin
        .box-header.title 入所者募集ページ
        .box-body
          .row.mb-3
            .col-12.d-flex.flex-row
              .d-flex.flex-row.justify-content-end.align-items-end.flex-fill
                button.btn.change-month-btn.mr-2(@click="onPrevWaitersGraph()") ＜ 前の月
                button.btn.change-month-btn.mr-2(@click="onCurrentWaitersGraph()") 今月
                button.btn.change-month-btn(@click="onNextWaitersGraph()"
                  :class="{ 'disable': disableOnNextWaitersGraph }" :disabled="disableOnNextWaitersGraph") 次の月 ＞
          .row
            .col-12
              LinesChart(:chart-data="waitersGraph" :height="waitersGraphHeight")
          Loading(ref="waitersGraphLoading")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { ChartData } from "chart.js";

import SystemError from "@/commons/models/SystemError";
import { errorHandle } from "@/errorHandler";

import WaitersService, { WaitersGetModel } from "@/commons/services/Waiters";
import { ResidentStatus } from "@/commons/services/Residents";
import {
  Template,
  TemplateItem,
  TemplateItemGroup,
  TemplateItemInput,
  TemplateItemRadio,
  TemplateItemRadioGroup,
  TemplateItemRoomType,
  TemplateItemSection,
  TemplateItemSwitchCheckbox,
  TemplateType
} from "@/commons/services/AdmissionApplicationTemplates";
import WaiterFunctionsService, { WaiterFunctionCountsGetModel, WaiterFunctionCountsModel, WaiterFunctionCountType } from "@/commons/services/WaiterFunctions";

import HomeCountBox from "@/pc/basics/HomeCountBox.vue";
import DoughnutChart from "@/pc/basics/DoughnutChart.vue";
import BarChart from "@/pc/basics/BarChart.vue";
import LinesChart from "@/pc/basics/LinesChart.vue";
import Loading from "@/pc/basics/Loading.vue";

/**
 * ホーム画面コンポーネント
 *
 */
@Component({
  components: {
    HomeCountBox,
    DoughnutChart,
    BarChart,
    LinesChart,
    Loading
  }
})
export default class HomeForWaitersService extends Vue {
  /** 待機者サービス */
  @Inject("WaitersService") waitersService!: WaitersService;
  /** 待機者機能使用数 */
  @Inject("WaiterFunctionsService") waiterFunctionsService!: WaiterFunctionsService;

  /** 要介護度 読込み中モーダル */
  @Ref() careLevelsGraphLoading!: Loading;
  /** 申込み部屋種類 読み込み中モーダル */
  @Ref() roomTypesGraphLoading!: Loading;
  /** 入所者募集ページ 読み込み中モーダル */
  @Ref() waitersGraphLoading!: Loading;

  /** 現在日 */
  currentDate: Date = new Date();

  /** 待機者数 */
  waiterCount: number = 0;
  /** 待機者数読込み中 */
  isBusyWaiterCount: boolean = true;
  /** 募集ページ閲覧（先月）数 */
  accessCountOfLastMonth: number = 0;
  /** 募集ページ閲覧（先月）数読込み中 */
  isBusyAccessCountOfLastMonth: boolean = true;
  /** 募集ページ閲覧（今月） */
  accessCountOfThisMonth: number = 0;
  /** 募集ページ閲覧（今月）数読込み中 */
  isBusyAccessCountOfThisMonth: boolean = true;
  /** 入所申込み（先月）数 */
  applicationsCountOfLastMonth: number = 0;
  /** 入所申込み（先月）数読込み中 */
  isBusyApplicationsCountOfLastMonth: boolean = true;
  /** 入所申込み（今月）数 */
  applicationsCountOfThisMonth: number = 0;
  /** 入所申込み（今月）数読込み中 */
  isBusyApplicationsCountOfThisMonth: boolean = true;
  /** 入所申込み（昨日）数 */
  applicationsCountOfYesterday: number = 0;
  /** 入所申込み（昨日）数読込み中 */
  isBusyApplicationsCountOfYesterday: boolean = true;

  /** 要介護度：グラフ */
  careLevelsGraph: ChartData = {};
  careLevelsGraphHeight: number = 208;

  /** 申込み部屋種類：グラフ */
  roomTypesGraph: ChartData = {};
  roomTypesGraphHeight: number = 208;

  /** 入所者募集ページ：グラフ */
  waitersGraphDate: Date = new Date();
  waitersGraph: ChartData = {};
  waitersGraphHeight: number = 440;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  public async createView(): Promise<void> {
    this.careLevelsGraphLoading.show();
    this.roomTypesGraphLoading.show();
    this.waitersGraphLoading.show();

    let results: Array<any> = await Promise.all([
      this.setWaiterCountGraph(),
      this.setLastMonthFunctionCounts(),
      this.setMonthlyFunctionCounts(),
      this.setMonthFunctionCountsForGraph()
    ]);
    // 非同期処理中に例外が発生していればresultの中にSystemerrorが格納されているのでチェックする
    for (const result of results) {
      if (result instanceof SystemError) {
        errorHandle(result, this);
      }
    }

    return;
  }

  /**
   * ### 待機者情報を使用した画面表示情報を設定する
   *
   * - 待機者数
   * - 要介護度グラフ
   * - 申込み部屋種類グラフ
   */
  public setWaiterCountGraph(): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const waiters: Array<WaitersGetModel> = await this.getWaiters();
        this.setWatersCount(waiters);
        this.setCareLevelsGraph(waiters);
        this.setRoomTypesGraph(waiters);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 待機者を取得する
   */
  public async getWaiters(): Promise<Array<WaitersGetModel>> {
    let response: Array<WaitersGetModel> = [];

    try {
      response = await this.waitersService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.waitersService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.waitersService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 待機者数を設定する
   */
  public setWatersCount(waiters: Array<WaitersGetModel>): void {
    const filterdWaiters: Array<WaitersGetModel> = waiters.filter((waiter) => {
      if (waiter.status == ResidentStatus.CANCELED || waiter.status == ResidentStatus.TEMPORARILY) {
        return false;
      } else {
        return true;
      }
    });
    this.waiterCount = filterdWaiters.length;
    this.isBusyWaiterCount = false;
    return;
  }

  /**
   * ### 要介護度グラフに表示する内容を作成する
   */
  public setCareLevelsGraph(waiters: Array<WaitersGetModel>): void {
    const filterdWaiters: Array<WaitersGetModel> = waiters.filter((waiter) => {
      if (waiter.status == ResidentStatus.CANCELED || waiter.status == ResidentStatus.TEMPORARILY) {
        return false;
      } else {
        return true;
      }
    });

    let careLebel1000Count: number = 0; // 自立
    let careLebel1010Count: number = 0; // 申請中
    let careLevel2010Count: number = 0; // 要支援１
    let careLevel2020Count: number = 0; // 要支援２
    let careLevel3010Count: number = 0; // 要介護1
    let careLevel3020Count: number = 0; // 要介護2
    let careLevel3030Count: number = 0; // 要介護3
    let careLevel3040Count: number = 0; // 要介護4
    let careLevel3050Count: number = 0; // 要介護5
    let otherCount: number = 0;

    for (const waiter of filterdWaiters) {
      const careLevel: string = this.getNursingCareLevel(waiter.waiter.admissionApplication.templateAnswer);
      switch (careLevel) {
        case "1000": {
          careLebel1000Count++;
          break;
        }
        case "1010": {
          careLebel1010Count++;
          break;
        }
        case "2010": {
          careLevel2010Count++;
          break;
        }
        case "2020": {
          careLevel2020Count++;
          break;
        }
        case "3010": {
          careLevel3010Count++;
          break;
        }
        case "3020": {
          careLevel3020Count++;
          break;
        }
        case "3030": {
          careLevel3030Count++;
          break;
        }
        case "3040": {
          careLevel3040Count++;
          break;
        }
        case "3050": {
          careLevel3050Count++;
          break;
        }
        default: {
          otherCount++;
          break;
        }
      }
    }

    // DoughnutChart表示用のObjectを作る
    let chartjsData: any = {
      labels: ["要介護５", "要介護４", "要介護３", "要介護２", "要介護１", "その他"],
      datasets: [
        {
          label: "要介護度",
          backgroundColor: ["#83CDD0", "#E6DC69", "#93BAFA", "#A1CE97", "#F8C0E5", "#FFEBD1"],
          borderWidth: [0, 0, 0, 0, 0, 0],
          data: [
            careLevel3050Count,
            careLevel3040Count,
            careLevel3030Count,
            careLevel3020Count,
            careLevel3010Count,
            careLebel1000Count + careLebel1010Count + careLevel2010Count + careLevel2020Count + otherCount
          ]
        }
      ],
      // DoughnutChartコンポーネントのcreateCenterText()でラベルサフィックスを渡す方法がこれしかないので無理やり格納している
      labelSuffix: "人"
    };

    this.careLevelsGraph = chartjsData;

    // 画面遷移でコンポーネントが存在しないタイミングがあるため、存在可否をチェックしてからローディングを非表示にする
    if (this.careLevelsGraphLoading) {
      this.careLevelsGraphLoading.hide();
    }

    return;
  }

  /**
   * ### 要介護度を返す
   */
  public getNursingCareLevel(templateAnswer: Template): string {
    const item: TemplateItemRadio | null = this.serachTemplateProperty(templateAnswer.properties, "careLevel") as TemplateItemRadio;
    if (item == null) {
      return "";
    }
    return item.answer;
  }

  /**
   * ### テンプレートのpropertiesから指定されたidの要素を返す
   */
  public serachTemplateProperty(properties: Array<TemplateItem>, idName: string): TemplateItemInput | null {
    for (const property of properties) {
      // 配下にperopetiesがあれば再帰検索
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const section: TemplateItemSection = property as TemplateItemSection;
          const item: TemplateItemInput | null = this.serachTemplateProperty(section.properties, idName);
          if (item != null) {
            return item;
          }
        } else if (property.type == TemplateType.GROUP) {
          const group: TemplateItemGroup = property as TemplateItemGroup;
          if (group.properties != null) {
            for (const childProperties of group.properties) {
              const item: TemplateItemInput | null = this.serachTemplateProperty(childProperties, idName);
              if (item != null) {
                return item;
              }
            }
          } else {
            return null;
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const radioGroup: TemplateItemRadioGroup = property as TemplateItemRadioGroup;
          if (radioGroup.properties != null) {
            for (const childProperties of radioGroup.properties) {
              if (childProperties.properties != null) {
                const item: TemplateItemInput | null = this.serachTemplateProperty(childProperties.properties, idName);
                if (item != null) {
                  return item;
                }
              }
            }
          } else {
            return null;
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const switchCheckbox: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;
          if (switchCheckbox.properties != null) {
            const item: TemplateItemInput | null = this.serachTemplateProperty(switchCheckbox.properties, idName);
            if (item != null) {
              return item;
            }
          } else {
            return null;
          }
        }
      }

      if (property.id == idName) {
        return property as TemplateItemInput;
      }
    }
    return null;
  }

  /**
   * ### propertiesを持つ要素か否か
   */
  public hasProperties(item: TemplateItem): boolean {
    switch (item.type) {
      case TemplateType.SECTION:
      case TemplateType.GROUP:
      case TemplateType.RADIO_GROUP:
      case TemplateType.SWITCH_CHECKBOX: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  /**
   * ### 申込み部屋種類グラフに表示する内容を作成する
   */
  public setRoomTypesGraph(waiters: Array<WaitersGetModel>): void {
    const labels: string[] = [];
    const roomTypeCountMap: Map<string, number> = new Map(); // 集計用MAP
    if (userStore.facility.recruitmentRoom.multiBed == true) {
      labels.push("従来型多床室");
      roomTypeCountMap.set("multiBed", 0);
    }
    if (userStore.facility.recruitmentRoom.conventinalSingle == true) {
      labels.push("従来型個室");
      roomTypeCountMap.set("conventinalSingle", 0);
    }
    if (userStore.facility.recruitmentRoom.unitTypeSingle == true) {
      labels.push("ユニット型個室");
      roomTypeCountMap.set("unitTypeSingle", 0);
    }
    if (userStore.facility.recruitmentRoom.single == true) {
      labels.push("個室");
      roomTypeCountMap.set("single", 0);
    }
    if (userStore.facility.recruitmentRoom.double == true) {
      labels.push("２人部屋（夫婦部屋）");
      roomTypeCountMap.set("double", 0);
    }
    if (userStore.facility.customRecruitmentRoom) {
      for (const customRoom of userStore.facility.customRecruitmentRoom) {
        if (customRoom.use == true) {
          labels.push(customRoom.name);
          roomTypeCountMap.set(customRoom.id, 0);
        }
      }
    }

    const filterdWaiters: Array<WaitersGetModel> = waiters.filter((waiter) => {
      if (waiter.status == ResidentStatus.CANCELED || waiter.status == ResidentStatus.TEMPORARILY) {
        return false;
      } else {
        return true;
      }
    });

    for (const waiter of filterdWaiters) {
      const roomType: TemplateItemRoomType | null = this.serachTemplateProperty(waiter.waiter.admissionApplication.templateAnswer.properties, "roomType") as TemplateItemRoomType;
      if (roomType == null) {
        continue;
      }

      for (const answer of roomType.answer) {
        if (userStore.facility.id == answer.facilityId) {
          if (userStore.facility.recruitmentRoom.multiBed == true && answer.recruitmentRoom.multiBed == true) {
            let count: number = roomTypeCountMap.get("multiBed") as number;
            roomTypeCountMap.set("multiBed", count + 1);
          }
          if (userStore.facility.recruitmentRoom.conventinalSingle == true && answer.recruitmentRoom.conventinalSingle == true) {
            let count: number = roomTypeCountMap.get("conventinalSingle") as number;
            roomTypeCountMap.set("conventinalSingle", count + 1);
          }
          if (userStore.facility.recruitmentRoom.unitTypeSingle == true && answer.recruitmentRoom.unitTypeSingle == true) {
            let count: number = roomTypeCountMap.get("unitTypeSingle") as number;
            roomTypeCountMap.set("unitTypeSingle", count + 1);
          }
          if (userStore.facility.recruitmentRoom.single == true && answer.recruitmentRoom.single == true) {
            let count: number = roomTypeCountMap.get("single") as number;
            roomTypeCountMap.set("single", count + 1);
          }
          if (userStore.facility.recruitmentRoom.double == true && answer.recruitmentRoom.double == true) {
            let count: number = roomTypeCountMap.get("double") as number;
            roomTypeCountMap.set("double", count + 1);
          }

          if (answer.customRecruitmentRoom) {
            for (const customRoom of answer.customRecruitmentRoom) {
              if (customRoom.use == true) {
                let count: number | undefined = roomTypeCountMap.get(customRoom.id);
                if (count != undefined) {
                  // undefinedで比較しないでif(count){}のように書くと0の場合に意図したルートを通らないので注意
                  roomTypeCountMap.set(customRoom.id, count + 1);
                }
              }
            }
          }
        }
      }
    }

    let data: number[] = []; // 集計結果
    if (userStore.facility.recruitmentRoom.multiBed == true) {
      data.push(roomTypeCountMap.get("multiBed") as number);
    }
    if (userStore.facility.recruitmentRoom.conventinalSingle == true) {
      data.push(roomTypeCountMap.get("conventinalSingle") as number);
    }
    if (userStore.facility.recruitmentRoom.unitTypeSingle == true) {
      data.push(roomTypeCountMap.get("unitTypeSingle") as number);
    }
    if (userStore.facility.recruitmentRoom.single == true) {
      data.push(roomTypeCountMap.get("single") as number);
    }
    if (userStore.facility.recruitmentRoom.double == true) {
      data.push(roomTypeCountMap.get("double") as number);
    }
    // labelsと同じ順に並べたいが、集計用プロパティはMAPなので、labelsと同じ順序は保証されない
    // そのためcustomRecruitmentRoomを再度ループしてlabelsと同じ順に集計結果を格納する
    if (userStore.facility.customRecruitmentRoom) {
      for (const customRoom of userStore.facility.customRecruitmentRoom) {
        if (customRoom.use == true) {
          data.push(roomTypeCountMap.get(customRoom.id) as number);
        }
      }
    }

    const chartjsData: any = {
      labels: labels,
      datasets: [
        {
          axis: "y", // 型定義だとaxisがないが、使用できることを確認したので型をanyにして設定している
          label: "申込み部屋種類",
          backgroundColor: "rgba(0, 140, 160, 1)",
          data: data
        }
      ]
    };

    this.roomTypesGraph = chartjsData;

    // 画面遷移でコンポーネントが存在しないタイミングがあるため、存在可否をチェックしてからローディングを非表示にする
    if (this.roomTypesGraphLoading) {
      this.roomTypesGraphLoading.hide();
    }

    return;
  }

  /**
   * ### 待機者機能使用数（先月分）を使用した画面表示情報を設定する
   *
   * - 募集ページ閲覧（先月）数
   * - 入所申込み（先月）数
   */
  public setLastMonthFunctionCounts(): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const lastMonthFunctionCounts: WaiterFunctionCountsGetModel = await this.getLastMonthFunctionCounts();
        this.setAccessCountOfLastMonth(lastMonthFunctionCounts);
        this.setApplicationsCountOfLastMonth(lastMonthFunctionCounts);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 先月分の待機者機能使用回数を取得する
   */
  public async getLastMonthFunctionCounts(): Promise<WaiterFunctionCountsGetModel> {
    const startDate: Date = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    const startAndEndDate: { start: number; end: number } = this.getStartEndDate(startDate);
    const response: WaiterFunctionCountsGetModel = await this.waiterFunctionsService.getCounts(userStore, {
      start: String(startAndEndDate.start),
      end: String(startAndEndDate.end)
    });
    return response;
  }

  /**
   * ### 指定月の初日、末日を返す
   *
   * - 以下のnumberで返す
   *   - 初日:yyyymmdd000000000
   *   - 末日:yyyymmdd235959000
   */
  public getStartEndDate(startDate: Date): { start: number; end: number } {
    startDate.setDate(1);
    const start: number = this.$dateUtil.convertDateToNumber(startDate, "yyyymmdd000000000");

    const endDate: Date = new Date(startDate.getTime());
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(endDate.getDate() - 1);
    const end: number = this.$dateUtil.convertDateToNumber(endDate, "yyyymmdd235959000");

    return { start, end };
  }

  /**
   * ### 募集ページ閲覧（先月）数を設定する
   */
  public setAccessCountOfLastMonth(lastMonthFunctionCounts: WaiterFunctionCountsGetModel): void {
    let count: number = 0;
    const startDate: Date = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    const startAndEndDate: { start: number; end: number } = this.getStartEndDate(startDate);
    const currentDate: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.start);
    const end: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.end);
    for (let date = 0; date < end.getDate(); date++) {
      currentDate.setDate(date + 1);

      const key: string = WaiterFunctionCountType.RECRUITMENT_PV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
      const functionCount: WaiterFunctionCountsModel | undefined = lastMonthFunctionCounts[key];
      if (functionCount != undefined) {
        count += functionCount.count;
      }
    }
    this.accessCountOfLastMonth = count;
    this.isBusyAccessCountOfLastMonth = false;
    return;
  }

  /**
   * ### 入所申込み（先月）数を設定する
   */
  public setApplicationsCountOfLastMonth(lastMonthFunctionCounts: WaiterFunctionCountsGetModel): void {
    let count: number = 0;
    const startDate: Date = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    const startAndEndDate: { start: number; end: number } = this.getStartEndDate(startDate);
    const currentDate: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.start);
    const end: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.end);
    for (let date = 0; date < end.getDate(); date++) {
      currentDate.setDate(date + 1);

      const userKey: string = WaiterFunctionCountType.ADMISSION_USER_CV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
      const userFunctionCount: WaiterFunctionCountsModel | undefined = lastMonthFunctionCounts[userKey];
      if (userFunctionCount != undefined) {
        count += userFunctionCount.count;
      }
      const facilityKey: string = WaiterFunctionCountType.ADMISSION_FACILITY_CV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
      const facilityFunctionCount: WaiterFunctionCountsModel | undefined = lastMonthFunctionCounts[facilityKey];
      if (facilityFunctionCount != undefined) {
        count += facilityFunctionCount.count;
      }
    }
    this.applicationsCountOfLastMonth = count;
    this.isBusyApplicationsCountOfLastMonth = false;
    return;
  }

  /**
   * ### 待機者機能使用数（今月分）を使用した画面表示情報を設定する
   *
   * - 募集ページ閲覧（今月）数
   * - 入所申込み（今月）数
   * - 入所申込み（昨日）数
   */
  public setMonthlyFunctionCounts(): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const monthlyFunctionCounts: WaiterFunctionCountsGetModel = await this.getMonthlyFunctionCounts();
        this.setAccessCountOfThisMonth(monthlyFunctionCounts);
        this.setApplicationsCountOfThisMonth(monthlyFunctionCounts);
        this.setApplicationsCountOfYesterday(monthlyFunctionCounts);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 今月分の待機者機能使用回数を取得する
   */
  public async getMonthlyFunctionCounts(): Promise<WaiterFunctionCountsGetModel> {
    const startDate: Date = new Date();
    const startAndEndDate: { start: number; end: number } = this.getStartEndDate(startDate);
    const response: WaiterFunctionCountsGetModel = await this.waiterFunctionsService.getCounts(userStore, {
      start: String(startAndEndDate.start),
      end: String(startAndEndDate.end)
    });
    return response;
  }

  /**
   * ### 募集ページ閲覧（今月）数を設定する
   */
  public setAccessCountOfThisMonth(monthlyFunctionCounts: WaiterFunctionCountsGetModel): void {
    let count: number = 0;
    const startDate: Date = new Date();
    const startAndEndDate: { start: number; end: number } = this.getStartEndDate(startDate);
    const currentDate: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.start);
    const end: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.end);
    for (let date = 0; date < end.getDate(); date++) {
      currentDate.setDate(date + 1);

      const key: string = WaiterFunctionCountType.RECRUITMENT_PV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
      const functionCount: WaiterFunctionCountsModel | undefined = monthlyFunctionCounts[key];
      if (functionCount != undefined) {
        count += functionCount.count;
      }
    }
    this.accessCountOfThisMonth = count;
    this.isBusyAccessCountOfThisMonth = false;
    return;
  }

  /**
   * ### 入所申込み（今月）数を設定する
   */
  public setApplicationsCountOfThisMonth(monthlyFunctionCounts: WaiterFunctionCountsGetModel): void {
    let count: number = 0;
    const startDate: Date = new Date();
    const startAndEndDate: { start: number; end: number } = this.getStartEndDate(startDate);
    const currentDate: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.start);
    const end: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.end);
    for (let date = 0; date < end.getDate(); date++) {
      currentDate.setDate(date + 1);

      const userKey: string = WaiterFunctionCountType.ADMISSION_USER_CV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
      const userFunctionCount: WaiterFunctionCountsModel | undefined = monthlyFunctionCounts[userKey];
      if (userFunctionCount != undefined) {
        count += userFunctionCount.count;
      }
      const facilityKey: string = WaiterFunctionCountType.ADMISSION_FACILITY_CV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
      const facilityFunctionCount: WaiterFunctionCountsModel | undefined = monthlyFunctionCounts[facilityKey];
      if (facilityFunctionCount != undefined) {
        count += facilityFunctionCount.count;
      }
    }
    this.applicationsCountOfThisMonth = count;
    this.isBusyApplicationsCountOfThisMonth = false;
    return;
  }

  /**
   * ### 入所申込み（昨日）数を設定する
   */
  public setApplicationsCountOfYesterday(monthlyFunctionCounts: WaiterFunctionCountsGetModel): void {
    let count: number = 0;
    const yesterday: Date = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const userKey: string = WaiterFunctionCountType.ADMISSION_USER_CV + "_" + this.$dateUtil.convertDateToString(yesterday, "yyyymmdd000000000");
    const userFunctionCount: WaiterFunctionCountsModel | undefined = monthlyFunctionCounts[userKey];
    if (userFunctionCount != undefined) {
      count += userFunctionCount.count;
    }
    const facilityKey: string = WaiterFunctionCountType.ADMISSION_FACILITY_CV + "_" + this.$dateUtil.convertDateToString(yesterday, "yyyymmdd000000000");
    const facilityFunctionCount: WaiterFunctionCountsModel | undefined = monthlyFunctionCounts[facilityKey];
    if (facilityFunctionCount != undefined) {
      count += facilityFunctionCount.count;
    }
    this.applicationsCountOfYesterday = count;
    this.isBusyApplicationsCountOfYesterday = false;
    return;
  }

  /**
   * ### 入所者募集ページグラフの画面表示情報を設定する
   */
  public setMonthFunctionCountsForGraph(): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const monthFunctionCountsForGraph: WaiterFunctionCountsGetModel = await this.getMonthFunctionCountsForGraph(this.waitersGraphDate);
        this.setWaitersGraph(monthFunctionCountsForGraph, this.waitersGraphDate);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 入所者募集ページグラフ用の待機者機能使用回数を取得する
   */
  public async getMonthFunctionCountsForGraph(startDate: Date): Promise<WaiterFunctionCountsGetModel> {
    const startAndEndDate: { start: number; end: number } = this.getStartEndDate(startDate);
    const response: WaiterFunctionCountsGetModel = await this.waiterFunctionsService.getCounts(userStore, {
      start: String(startAndEndDate.start),
      end: String(startAndEndDate.end)
    });
    return response;
  }

  /**
   * ### 待機者グラフに表示する内容を作成する
   */
  public setWaitersGraph(monthFunctionCountsForGraph: WaiterFunctionCountsGetModel, startDate: Date): void {
    const today: Date = new Date();
    const startAndEndDate: { start: number; end: number } = this.getStartEndDate(startDate);
    const currentDate: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.start);
    const end: Date = this.$dateUtil.convertNumberToDate(startAndEndDate.end);

    // ラベル
    let labels: string[] = [];
    let waiterCountsData: Array<number | null> = [];
    let recruitmentCountsData: Array<number | null> = [];
    let admissionPageCountsData: Array<number | null> = [];
    let admissionCountsData: Array<number | null> = [];
    for (let date = 0; date < end.getDate(); date++) {
      currentDate.setDate(date + 1);

      // ラベル
      labels.push(this.$dateUtil.convertDateToString(currentDate, "mm/dd", false));

      // 待機者数
      const waiterKey: string = WaiterFunctionCountType.WAITERS + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
      const waiterFunctionCount: WaiterFunctionCountsModel | undefined = monthFunctionCountsForGraph[waiterKey];
      if (waiterFunctionCount != undefined) {
        waiterCountsData.push(waiterFunctionCount.count);
      } else {
        waiterCountsData.push(null); // 集計されていない場合、グラフの点を消したいのでnullを格納する
      }

      // 未来日以降はデータ無しにする
      const currentDateNumber: number = this.$dateUtil.convertDateToNumber(currentDate, "yyyymmdd");
      const todayNumber: number = this.$dateUtil.convertDateToNumber(today, "yyyymmdd");
      if (todayNumber < currentDateNumber) {
        recruitmentCountsData.push(null);
        admissionPageCountsData.push(null);
        admissionCountsData.push(null);
      }
      // 当日までは集計データを格納する
      else {
        // 募集ページ閲覧数
        const recruitmentKey: string = WaiterFunctionCountType.RECRUITMENT_PV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
        const recruitmentFunctionCount: WaiterFunctionCountsModel | undefined = monthFunctionCountsForGraph[recruitmentKey];
        if (recruitmentFunctionCount != undefined) {
          recruitmentCountsData.push(recruitmentFunctionCount.count);
        } else {
          recruitmentCountsData.push(0);
        }
        // 申込みページ閲覧数
        const admissionPageKey: string = WaiterFunctionCountType.ADMISSION_PV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
        const admissionPageFunctionCount: WaiterFunctionCountsModel | undefined = monthFunctionCountsForGraph[admissionPageKey];
        if (admissionPageFunctionCount != undefined) {
          admissionPageCountsData.push(admissionPageFunctionCount.count);
        } else {
          admissionPageCountsData.push(0);
        }
        // 入所申込数
        let admissionCount: number = 0;
        const admissionUserKey: string = WaiterFunctionCountType.ADMISSION_USER_CV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
        const admissionUserFunctionCount: WaiterFunctionCountsModel | undefined = monthFunctionCountsForGraph[admissionUserKey];
        if (admissionUserFunctionCount != undefined) {
          admissionCount += admissionUserFunctionCount.count;
        } else {
          admissionCount += 0;
        }
        const admissionFacilityKey: string = WaiterFunctionCountType.ADMISSION_FACILITY_CV + "_" + this.$dateUtil.convertDateToString(currentDate, "yyyymmdd000000000");
        const admissionFacilityFunctionCount: WaiterFunctionCountsModel | undefined = monthFunctionCountsForGraph[admissionFacilityKey];
        if (admissionFacilityFunctionCount != undefined) {
          admissionCount += admissionFacilityFunctionCount.count;
        } else {
          admissionCount += 0;
        }
        admissionCountsData.push(admissionCount);
      }
    }

    const chartjsData: any = {
      labels: labels, // 横軸のラベル
      datasets: [
        {
          label: "待機者数",
          data: waiterCountsData,
          fill: false,
          pointBackgroundColor: "#83CDD0",
          borderColor: "#83CDD0",
          lineTension: 0
        },
        {
          label: "募集ページ閲覧",
          data: recruitmentCountsData,
          fill: false,
          pointBackgroundColor: "#E6DC69",
          borderColor: "#E6DC69",
          lineTension: 0
        },
        {
          label: "申込みページ閲覧",
          data: admissionPageCountsData,
          fill: false,
          pointBackgroundColor: "#93BAFA",
          borderColor: "#93BAFA",
          lineTension: 0
        },
        {
          label: "入所申込み",
          data: admissionCountsData,
          fill: false,
          pointBackgroundColor: "#A1CE97",
          borderColor: "#A1CE97",
          lineTension: 0
        }
      ]
    };

    this.waitersGraph = chartjsData;

    // 画面遷移でコンポーネントが存在しないタイミングがあるため、存在可否をチェックしてからローディングを非表示にする
    if (this.waitersGraphLoading) {
      this.waitersGraphLoading.hide();
    }

    return;
  }

  /**
   * ### 待機者グラフの「次の月」ボタン非活性化を返す
   */
  public disableOnNextWaitersGraph(): boolean {
    const todayTime: number = new Date().getTime();
    const nextGraphDate: Date = new Date(this.waitersGraphDate.getTime());
    nextGraphDate.setDate(nextGraphDate.getDate() + 1);
    const nextCurrentTime: number = nextGraphDate.getTime();
    if (todayTime <= nextCurrentTime) {
      return true;
    }
    return false;
  }

  /**
   * ### 待機者グラフの「前の月」ボタン押下イベント
   */
  public async onPrevWaitersGraph(): Promise<void> {
    this.waitersGraphLoading.show();
    this.waitersGraphDate.setMonth(this.waitersGraphDate.getMonth() - 1);
    await this.setMonthFunctionCountsForGraph();
    return;
  }

  /**
   * ### 待機者グラフの「今月」ボタン押下イベント
   */
  public async onCurrentWaitersGraph(): Promise<void> {
    this.waitersGraphLoading.show();
    this.waitersGraphDate = new Date();
    await this.setMonthFunctionCountsForGraph();
    return;
  }

  /**
   * ### 待機者グラフの「次の月」ボタン押下イベント
   */
  public async onNextWaitersGraph(): Promise<void> {
    this.waitersGraphLoading.show();
    this.waitersGraphDate.setMonth(this.waitersGraphDate.getMonth() + 1);
    await this.setMonthFunctionCountsForGraph();
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/pc/_mixin.scss";
@import "../../styles/pc/_colors.scss";

#home-for-waiters-service {
  .facility-base-info {
    margin-left: -3px;
    margin-right: -3px;
    .home-box {
      padding-left: 3px;
      padding-right: 3px;
    }
  }
  .box {
    .title {
      @include bold-font();
      padding: 10px 0 0 10px;
    }
  }

  .graphs {
    .carelevels-and-roomtypes {
      .box-body {
        position: relative;
        height: 238px;
      }

      @media (min-width: 992px) {
        padding-right: 3px !important;
        margin-bottom: 0 !important;
      }
      @media (max-width: 992px) {
        padding-right: 15px !important;
        margin-bottom: 16px !important;
      }
    }
    .waiters-graph {
      .box-body {
        position: relative;
        height: 526px;
      }

      @media (min-width: 992px) {
        padding-left: 3px !important;
        margin-bottom: 0 !important;
      }
      .date {
        @include font(20px);
        margin: 0 20px;
      }
      .change-month-btn {
        min-width: 104px;
      }
    }
  }
}
</style>
